import { load } from '@fingerprintjs/fingerprintjs-pro';
import pRetry from 'p-retry';
import { addBreadcrumb, logError } from './observability';

/**
 * `linkedId` is what lights up Fingerprint's linked-ID velocity signals (distinct devices and
 * IPs per account); without it those come back empty.
 */
export async function getDeviceFingerprint(linkedId?: string) {
  if (import.meta.env.VITE_NODE_ENV !== 'prod') return undefined; // skip fingerprinting in non-prod environments, can always be commented out to test locally
  try {
    const apiKey = import.meta.env.VITE_FINGERPRINT_API_KEY;
    if (!apiKey) throw new Error('Fingerprint API Key Not Provided');

    const fingerprintAgent = await load({
      apiKey,
      region: 'us',
      /** @see https://dev.fingerprint.com/reference/js-agent-load-function#scripturlpattern */
      endpoint: 'https://fingerprint.corso.com',
      scriptUrlPattern:
        'https://fingerprint.corso.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js',
    });

    const result = await pRetry(
      () =>
        fingerprintAgent.get(
          linkedId ? { linkedId: linkedId.trim().toLowerCase() } : {},
        ),
      {
        // cRetry's `request` policy — flat 150ms, max 300ms of sleep. cRetry itself can't
        // cross into the browser bundle: it pulls in winston and the node Sentry SDK.
        retries: 2,
        minTimeout: 150,
        factor: 1,
        onFailedAttempt: (error) => {
          addBreadcrumb({
            message: `Fingerprint attempt ${error.attemptNumber} failed. Retries left: ${error.retriesLeft}`,
            level: 'warning',
            data: {
              attemptNumber: error.attemptNumber,
              retriesLeft: error.retriesLeft,
              errorName: error.error.name,
              errorMessage: error.error.message,
            },
          });
        },
      },
    );

    return {
      visitorId: result.visitorId,
      requestId: result.requestId,
    };
  } catch (error) {
    logError(new Error('Error Getting Fingerprint', { cause: error }));
    return undefined;
  }
}
